import { useMemo, useState } from 'react';
import {
  Content,
  ErrorPanel,
  Header,
  HeaderLabel,
  Page,
  Table,
  TableColumn,
  TableOptions,
} from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import {
  Entity,
  parseEntityRef,
  RELATION_OWNED_BY,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import {
  catalogApiRef,
  EntityRefLink,
  getEntityRelations,
  humanizeEntityRef,
} from '@backstage/plugin-catalog-react';
import { ScorecardsList } from '@backstage-community/plugin-tech-insights';
import {
  BulkCheckResponse,
  CheckResult,
} from '@backstage-community/plugin-tech-insights-common';
import { techInsightsApiRef } from '@backstage-community/plugin-tech-insights-react';
import { ExportCsv as exportCsv } from '@material-table/exporters';
import Grid from '@material-ui/core/Grid';
import useAsync from 'react-use/lib/useAsync';
import { isUndetermined } from '../../lib/techInsightsCheckResultRenderer';
import { Filters } from './Filters';
import {
  defaultSelection,
  filterOptionsFromRows,
  filterScorecardRows,
  ScorecardRow,
  ScorecardSelection,
  sortByCheckResults,
  statusOptions,
} from './scorecardFilters';

/** Catalog fields the sidebar needs. Anything else is left on the server. */
const ENTITY_FIELDS = [
  'kind',
  'metadata.name',
  'metadata.namespace',
  'metadata.title',
  'relations',
];

/**
 * Pairs a bulk check response with the entity it ran against.
 *
 * The bulk endpoint only returns refs, so anything the sidebar filters on beyond
 * the check results comes from the catalog. An entity can be missing here: it can
 * be removed between the check run and this fetch, and permissions can hide it.
 * The ref still names it well enough to show and filter on.
 */
export const scorecardRow = (
  response: BulkCheckResponse[0],
  entity: Entity | undefined,
): ScorecardRow => {
  const ref = parseEntityRef(response.entity);

  return {
    entityRef: response.entity,
    label:
      entity?.metadata.title ??
      humanizeEntityRef(entity ?? ref, { defaultKind: ref.kind }),
    kind: entity?.kind ?? ref.kind,
    owners: getEntityRelations(entity, RELATION_OWNED_BY).map(owner => ({
      value: stringifyEntityRef(owner),
      label: humanizeEntityRef(owner, { defaultKind: 'Group' }),
    })),
    results: response.results,
  };
};

/**
 * The global scorecards page, forked from `TechInsightsScorecardPage` in
 * @backstage-community/plugin-tech-insights v1.4.0.
 *
 * It differs from upstream in three ways. The sidebar filters only ever narrow,
 * so no combination of them returns an empty table for a reason the sidebar does
 * not show; upstream's two Yes/No dropdowns both restricted the same axis, which
 * made two of their four combinations unsatisfiable. It filters on owner, kind,
 * entity and failing check as well as status. And it asks whether a result failed
 * rather than reading its raw boolean, so a check the backend could not evaluate
 * stops counting as a failure, matching what an entity's own scorecard shows.
 *
 * Only the "Checks to run" filter reaches the backend. The rest narrow rows the
 * page already has, so they apply without a refetch.
 *
 * Upstream's `badge` prop is left out, since nothing in this app passed it.
 */
export const ScorecardsPage = (props: { dense?: boolean }) => {
  const api = useApi(techInsightsApiRef);
  const catalogApi = useApi(catalogApiRef);
  const [selection, setSelection] =
    useState<ScorecardSelection>(defaultSelection);
  const { checkIds } = selection;

  const predicates = useMemo(
    () => ({
      isFailed: (result: CheckResult) => api.isCheckResultFailed(result),
      isUndetermined,
    }),
    [api],
  );

  const tableOptions: TableOptions = {
    exportAllData: true,
    exportMenu: [
      {
        label: 'Export CSV',
        exportFunc: (cols, datas) => exportCsv(cols, datas, 'tech-insights'),
      },
    ],
    pageSize: 5,
    pageSizeOptions: [5, 10, 20],
    padding: props.dense ? 'dense' : 'default',
    thirdSortClick: false,
  };

  const { value, loading, error } = useAsync(async () => {
    const checks = await api.getAllChecks();
    const responses = await api.runBulkChecks(
      [],
      checks.filter(check => checkIds.includes(check.id)),
    );

    const entityRefs = responses.map(response => response.entity);
    const { items } = entityRefs.length
      ? await catalogApi.getEntitiesByRefs({
          entityRefs,
          fields: ENTITY_FIELDS,
        })
      : { items: [] };

    return {
      checks,
      rows: responses.map((response, index) =>
        scorecardRow(response, items[index]),
      ),
    };
  }, [api, catalogApi, checkIds]);

  const checks = useMemo(() => value?.checks ?? [], [value]);
  const rows = useMemo(() => value?.rows ?? [], [value]);

  const options = useMemo(
    () => ({
      statuses: statusOptions,
      checks: checks.map(check => ({ value: check.id, label: check.name })),
      ...filterOptionsFromRows(rows, predicates),
    }),
    [checks, rows, predicates],
  );

  const filteredRows = useMemo(
    () => filterScorecardRows(rows, selection, predicates),
    [rows, selection, predicates],
  );

  const tableColumns = useMemo(() => {
    const columns: TableColumn<ScorecardRow>[] = [
      {
        field: 'entityRef',
        title: 'Entity',
        render: row => <EntityRefLink entityRef={row.entityRef} />,
      },
      {
        field: 'results',
        title: 'Results',
        render: row => (
          <ScorecardsList checkResults={row.results} dense={props.dense} />
        ),
        defaultSort: 'desc',
        customSort: sortByCheckResults(predicates),
        export: false,
      },
    ];

    // Scoping which checks run also scopes which columns the CSV carries.
    const exportedChecks =
      checkIds.length === 0
        ? checks
        : checks.filter(check => checkIds.includes(check.id));

    exportedChecks.forEach(check => {
      columns.push({
        field: check.id,
        title: check.name,
        customExport: row =>
          `${
            row.results.filter(
              result => result && result.check && result.check.id === check.id,
            )[0]?.result
          }`,
        hidden: true,
        export: true,
      });
    });

    return columns;
  }, [props.dense, checks, checkIds, predicates]);

  if (error) {
    return <ErrorPanel error={error} />;
  }

  return (
    <Page themeId="tool">
      <Header title="Tech insights">
        <HeaderLabel label="Entities" value={filteredRows.length} />
        <HeaderLabel label="Checks" value={checks.length} />
      </Header>
      <Content>
        <Grid container>
          <Grid item style={{ width: '300px' }}>
            <Filters
              options={options}
              selection={selection}
              selectionChanged={setSelection}
              loading={loading}
            />
          </Grid>
          <Grid item xs>
            <Table
              columns={tableColumns}
              data={filteredRows}
              isLoading={loading}
              options={tableOptions}
            />
          </Grid>
        </Grid>
      </Content>
    </Page>
  );
};
